import React, { useState, useEffect } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, Check, Repeat, Calculator } from "lucide-react";

const categoriasCusto = [
  { value: "insumos", label: "Insumos", icon: "🌱" },
  { value: "mao_obra", label: "Mão de Obra", icon: "👷" },
  { value: "combustivel", label: "Combustível", icon: "⛽" },
  { value: "manutencao", label: "Manutenção", icon: "🔧" },
  { value: "energia", label: "Energia", icon: "⚡" },
  { value: "agua", label: "Água", icon: "💧" },
  { value: "aluguel", label: "Aluguel/Arrendamento", icon: "🏠" },
  { value: "transporte", label: "Transporte/Frete", icon: "🚛" },
  { value: "seguro", label: "Seguros", icon: "🛡️" },
  { value: "impostos", label: "Impostos/Taxas", icon: "🏛️" },
  { value: "consultoria", label: "Consultoria", icon: "📋" },
  { value: "depreciacao", label: "Depreciação", icon: "📉" },
  { value: "outros", label: "Outros", icon: "📦" }
];

export default function CustoAlocadoForm({ custo, centrosCusto, safras, talhoes, onSuccess, onCancel }) {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");
  
  const [formData, setFormData] = useState({
    descricao: "",
    valor: "",
    data: new Date().toISOString().split('T')[0],
    tipo_custo: "variavel",
    categoria: "",
    centro_custo_id: "",
    safra_id: "",
    talhao_id: "",
    recorrente: false,
    frequencia: "mensal",
    projecao_meses: "",
    unidade_medida: "ha",
    quantidade_unidades: "",
    custo_unitario: "",
    observacoes: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (custo) {
      setFormData({
        descricao: custo.descricao || "",
        valor: custo.valor?.toString() || "",
        data: custo.data || "",
        tipo_custo: custo.tipo_custo || "variavel",
        categoria: custo.categoria || "",
        centro_custo_id: custo.centro_custo_id || "",
        safra_id: custo.safra_id || "",
        talhao_id: custo.talhao_id || "",
        recorrente: custo.recorrente || false,
        frequencia: custo.frequencia || "mensal",
        projecao_meses: custo.projecao_meses?.toString() || "",
        unidade_medida: custo.unidade_medida || "ha",
        quantidade_unidades: custo.quantidade_unidades?.toString() || "",
        custo_unitario: custo.custo_unitario?.toString() || "",
        observacoes: custo.observacoes || ""
      });
    }
  }, [custo]);

  // Calcular valor automaticamente
  useEffect(() => {
    if (formData.quantidade_unidades && formData.custo_unitario) {
      const valor = parseFloat(formData.quantidade_unidades) * parseFloat(formData.custo_unitario);
      setFormData(prev => ({ ...prev, valor: valor.toFixed(2) }));
    }
  }, [formData.quantidade_unidades, formData.custo_unitario]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const data = {
      ...formData,
      propriedade_id: propriedadeId,
      valor: parseFloat(formData.valor) || 0,
      projecao_meses: formData.projecao_meses ? parseInt(formData.projecao_meses) : null,
      quantidade_unidades: formData.quantidade_unidades ? parseFloat(formData.quantidade_unidades) : null,
      custo_unitario: formData.custo_unitario ? parseFloat(formData.custo_unitario) : null
    };
    
    if (custo) {
      await agrogestao.entities.CustoAlocado.update(custo.id, data);
    } else {
      await agrogestao.entities.CustoAlocado.create(data);
    }
    
    setSaving(false);
    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Descrição */}
      <div className="space-y-2">
        <Label>Descrição *</Label>
        <Input
          placeholder="Ex: Fertilizante NPK, Diesel colheita..."
          value={formData.descricao}
          onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
          required
        />
      </div>

      {/* Tipo e Categoria */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Tipo de Custo *</Label>
          <Select value={formData.tipo_custo} onValueChange={(v) => setFormData({ ...formData, tipo_custo: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fixo">🔒 Custo Fixo</SelectItem>
              <SelectItem value="variavel">📊 Custo Variável</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Categoria *</Label>
          <Select value={formData.categoria} onValueChange={(v) => setFormData({ ...formData, categoria: v })} required>
            <SelectTrigger>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              {categoriasCusto.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.icon} {c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Centro de Custo e Data */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Centro de Custo</Label>
          <Select value={formData.centro_custo_id} onValueChange={(v) => setFormData({ ...formData, centro_custo_id: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione (opcional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={null}>Nenhum</SelectItem>
              {centrosCusto.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Data *</Label>
          <Input
            type="date"
            value={formData.data}
            onChange={(e) => setFormData({ ...formData, data: e.target.value })}
            required
          />
        </div>
      </div>

      {/* Safra e Talhão */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Safra</Label>
          <Select value={formData.safra_id} onValueChange={(v) => setFormData({ ...formData, safra_id: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Vincular a safra" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={null}>Nenhuma</SelectItem>
              {safras.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.nome} - {s.cultura}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Talhão</Label>
          <Select value={formData.talhao_id} onValueChange={(v) => setFormData({ ...formData, talhao_id: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Vincular a talhão" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={null}>Nenhum</SelectItem>
              {talhoes.map(t => (
                <SelectItem key={t.id} value={t.id}>{t.nome} ({t.area_hectares} ha)</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Cálculo por Unidade */}
      <div className="p-4 bg-purple-50 rounded-xl space-y-3">
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-purple-600" />
          <span className="font-medium text-purple-800">Cálculo por Unidade (opcional)</span>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Unidade</Label>
            <Select value={formData.unidade_medida} onValueChange={(v) => setFormData({ ...formData, unidade_medida: v })}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ha">Hectare</SelectItem>
                <SelectItem value="saca">Saca</SelectItem>
                <SelectItem value="cabeca">Cabeça</SelectItem>
                <SelectItem value="litro">Litro</SelectItem>
                <SelectItem value="kg">Kg</SelectItem>
                <SelectItem value="unidade">Unidade</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Quantidade</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="Ex: 100"
              value={formData.quantidade_unidades}
              onChange={(e) => setFormData({ ...formData, quantidade_unidades: e.target.value })}
              className="h-9"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Custo Unitário (R$)</Label>
            <Input
              type="number"
              step="0.01"
              placeholder="Ex: 50.00"
              value={formData.custo_unitario}
              onChange={(e) => setFormData({ ...formData, custo_unitario: e.target.value })}
              className="h-9"
            />
          </div>
        </div>
      </div>

      {/* Valor Total */}
      <div className="space-y-2">
        <Label>Valor Total (R$) *</Label>
        <Input
          type="number"
          step="0.01"
          placeholder="0,00"
          value={formData.valor}
          onChange={(e) => setFormData({ ...formData, valor: e.target.value })}
          required
          className="text-lg font-semibold"
        />
      </div>

      {/* Recorrência e Projeção */}
      <div className="p-4 bg-amber-50 rounded-xl space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Repeat className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-medium">Custo Recorrente</p>
              <p className="text-xs text-stone-500">Projetar para meses futuros</p>
            </div>
          </div>
          <Switch
            checked={formData.recorrente}
            onCheckedChange={(checked) => setFormData({ ...formData, recorrente: checked })}
          />
        </div>
        
        {formData.recorrente && (
          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-amber-200">
            <div className="space-y-2">
              <Label>Frequência</Label>
              <Select value={formData.frequencia} onValueChange={(v) => setFormData({ ...formData, frequencia: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensal">Mensal</SelectItem>
                  <SelectItem value="bimestral">Bimestral</SelectItem>
                  <SelectItem value="trimestral">Trimestral</SelectItem>
                  <SelectItem value="semestral">Semestral</SelectItem>
                  <SelectItem value="anual">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Projetar para (meses)</Label>
              <Input
                type="number"
                placeholder="Ex: 12"
                value={formData.projecao_meses}
                onChange={(e) => setFormData({ ...formData, projecao_meses: e.target.value })}
              />
            </div>
          </div>
        )}
      </div>

      {/* Observações */}
      <div className="space-y-2">
        <Label>Observações</Label>
        <Textarea
          placeholder="Informações adicionais..."
          value={formData.observacoes}
          onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
          rows={2}
        />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">Cancelar</Button>
        <Button type="submit" disabled={saving} className="flex-1 bg-purple-600 hover:bg-purple-700">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" />{custo ? "Atualizar" : "Cadastrar"}</>}
        </Button>
      </div>
    </form>
  );
}

export { categoriasCusto };